<template>
  <fin-data-loader :resolver="loadData">
    <template #content>

      <!-- Payment method -->
      <fin-my-profile-block title="Zahlungsinformationen und Rechnungen">
        <p v-if="!isApp">
          <a :href="paymentPortalUrl" target="_self">
            Hier</a>
          hast du Zugriff auf deine Rechnungen und kannst deine Zahlungsinformationen einsehen und gegebenenfalls
          anpassen.
        </p>
        <p v-else>Auf <a :href="paymentsWebUrl">fincraft.de</a> hast du Zugriff auf deine Rechnungen und kannst deine
          Zahlungsinformationen einsehen und gegebenenfalls
          anpassen.</p>

        <!-- invoices -->
        <div v-if="!isApp && profile.paymentStatus === 'open'">
          <p>
            <v-icon class="mr-1"
                    :color="$finColors.primaryDark">icon-information
            </v-icon>
            Eine offene Rechnung liegt vor.
          </p>
          <p v-if="openInvoicesDueDatePast">
            Dein Konto wurde gesperrt, da deine offene Rechnung nicht beglichen wurde.
            Die Zahlungsfrist war {{ profile.paymentDeadline | date }}.
          </p>
          <p v-else>
            Solltest du deine offene Rechnung bis zum {{ profile.paymentDeadline | date }} nicht beglichen haben,
            wird dein Konto gesperrt.
          </p>
          <p>
            Bitte passe deine Zahlungsdaten an,
            oder setze dich bei Fragen zur Zahlung oder zum Abo mit unserem Support
            <a href="mailto:support@fincraft.de">support@fincraft.de</a> in Verbindung.
          </p>
          <fin-data-table v-if="showInvoicesTable" :resolver="invoices" :headers="invoicesTableHeaders"/>
        </div>
      </fin-my-profile-block>

      <!-- Deadlines -->
      <fin-my-profile-block v-if="!isApp" title="Deine Fristen">
        <p v-if="!isInDowngradePhase && !hasFailedInvoicePayment">
          Du musst derzeit keine Fristen beachten.
        </p>

        <!-- TODO check if hasFailedInvoicePayment and paymentDeadline get the right values and are displayed properly -->
        <p v-if="hasFailedInvoicePayment">
          Zahlung fehlgeschlagen (weitere Details findest du <a :href="paymentPortalUrl" target="_self">hier</a>...).
          <span v-if="paymentDeadline">Bitte überprüfe, bzw. korrigiere deine Zahlungsmethode bis
          {{ paymentDeadline | date }}. </span>
          <br/>
          <fin-lexicon-link class="mt-4" to="failedpayment">
            Was passiert, wenn die Zahlung nicht bis zur genannten Frist erfolgt ist?
          </fin-lexicon-link>
        </p>

        <p v-if="isInDowngradePhase">
          Aufgrund deines Downgrades musst du die Anzahl deiner Portfolios und Watchlists
          <span v-if="userItemsManagementDeadline">bis zum {{ userItemsManagementDeadline | date }} </span> anpassen.
          <br/>
          <fin-lexicon-link class="mt-4" to="portfoliosperre-downgrade">
            Warum muss ich die Anzahl der Portfolios/Watchlists anpassen und
            was passiert mit deinen Portfolios und Watchlists
            <span v-if="userItemsManagementDeadline"> am {{userItemsManagementDeadline | date}}</span>
          </fin-lexicon-link>
        </p>
      </fin-my-profile-block>
    </template>
  </fin-data-loader>
</template>

<script>
import FinDataLoader from "@/components/common/DataLoader"
import FinMyProfileBlock from "@/components/pages/profile/MyProfileBlock"
import finBrowser from "@/js/finBrowser"
import {mapGetters, mapState} from "vuex"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import {getInvoices, getPaymentPortalLink} from "@/components/pages/profile/subscription/graphql"
import FinDataTable from "@/components/common/DataTable/FinDataTable"
import {TablePresets} from "@/components/common/DataTable/DefaultHeaders"
import FinLexiconLink from "@/components/common/lexicon/LexiconLink"
import {listPriceModels} from "@/components/pages/profile/graphql"
import {getUserAboPaymentDeadlines} from "@/js/finQueries"
import BaseUrl from "@/mixins/BaseUrl"

export default {
  name: "FinUserPayments",
  components: {FinLexiconLink, FinDataTable, FinMyProfileBlock, FinDataLoader},
  data() {
    return {
      paymentPortalUrl: null,
      isApp: finBrowser.IS_IOS_OR_ANDROID_APP,
      invoices: [],
      priceModels: [],
      paymentDeadline: undefined,
      hasFailedInvoicePayment: false,
      isInDowngradePhase: false,
      // deadline for the user to activate/deactivate the portfolios/watchlists of his choice during his downgrade process
      userItemsManagementDeadline: undefined,
      showInvoicesTable: false,
      openInvoicesDueDatePast: false,
      paymentsWebUrl: this.getBaseURL() + "/protected/myProfile/payments",
    }
  },
  mixins: [BaseUrl],
  computed: {
    ...mapGetters('auth', ['userId', 'groupPriceModel', 'profile']),
    ...mapState({
      profile: state => state.auth.profile
    }),
    invoicesTableHeaders() {
      return TablePresets.createInvoicesHeaders()
    },
  },
  methods: {
    async loadData() {
      if (!this.isApp) {

        await this.getPaymentPortalUrl()
        if (this.showInvoicesTable) {
          await this.getInvoices()
        }
        await this.getPriceModels()
        await this.getPaymentDeadlinesInfos()

        this.userItemsManagementDeadline = new Date(this.profile.subscriptionChangeDate).setDate(new Date(this.profile.subscriptionChangeDate).getDate() + 14)
        this.checkAndHandleSubscriptionChangeIsADowngrade()
      }
    },
    async getPaymentPortalUrl() {
      try {
        const result = await API.graphql(graphqlOperation(getPaymentPortalLink, {
          returnUrl: this.paymentsWebUrl
        }))
        if (result) {
          return this.paymentPortalUrl = result.data.getPaymentPortalLink
        }
      } catch (err) {
        console.error("failed to get the payment portal url", err)
      }
    },
    getStatusText(status) {
      const mappedStatusText = [
        {status: 'OPEN', text: 'offen'},
        {status: 'UNCOLLECTIBLE', text: 'höchstwahrscheinlich nicht bezahlt'},
      ]
      const mappedText = mappedStatusText.find(s => s.status === status)?.text
      return mappedText ? mappedText : '-'
    },
    async getInvoices() {
      try {
        const result = await API.graphql(graphqlOperation(getInvoices, {
          status: 'OPEN',
        }))
        console.log("getInvoices result", result.data.getInvoices)
        if (result) {
          this.invoices = result.data.getInvoices
          this.invoices = this.invoices.map(i => ({
            ...i,
            statusText: this.getStatusText(i.status)
          }))
        }
      } catch (err) {
        console.error("failed to get the invoices", err)
      }
    },
    async getPriceModels() {
      await API.graphql({
        query: listPriceModels,
        //authMode: store.getters["auth/publicQueryAuthMode"]
      }).then(result => {
        if (result) {
          this.priceModels = result.data.listPriceModels.items.filter(i => i.id !== 'DEVELOPER')
        }
      }).catch(err => {
        console.error("failed to get the price models", err)
      })
    },
    async getPaymentDeadlinesInfos() {
      await API.graphql(graphqlOperation(getUserAboPaymentDeadlines, {
        id: this.userId,
      })).then(result => {
        if (result) {
          this.paymentDeadline = result.data.getUser.paymentDeadline ? result.data.getUser.paymentDeadline : undefined
        }
      }).catch(err => {
        console.error("failed to get the payment deadlines infos", err)
      })

      this.hasFailedInvoicePayment = this.paymentDeadline !== undefined
    },
    checkAndHandleSubscriptionChangeIsADowngrade() {
      let isInDowngradePhase = false
      if (this.profile.subscriptionChange && this.profile.subscriptionChange !== this.groupPriceModel.id) {
        const oldPriceModel = this.priceModels.find(i => i.id === this.groupPriceModel.id)
        const newPriceModel = this.priceModels.find(i => i.id === this.profile.subscriptionChange)
        if (oldPriceModel && oldPriceModel.order > newPriceModel.order) {
          isInDowngradePhase = true
        }
      }
      this.isInDowngradePhase = isInDowngradePhase
    }
  },
  mounted() {
    const today = new Date()
    const todayMs = new Date(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`).getTime()
    const openInvoicesDueDateMS = new Date(this.profile.paymentDeadline).getTime()
    this.openInvoicesDueDatePast = todayMs > openInvoicesDueDateMS
  }
}
</script>

<style lang="scss" scoped>

</style>
